class SliderComponent extends HTMLElement {
    constructor() {
        super();
        this.slider = null;
        this.sliderItems = [];
        this.dotButton = [];
        this.rtl_num = document.body.classList.contains('layout_rtl') ? -1 : 1;
        this.boundUpdate = this.update.bind(this);
        this.boundButtonClick = this.onButtonClick.bind(this);
        this.boundDotClick = this.onDotClick.bind(this);
        this.resizeObserver = new ResizeObserver(() => this.initPages());
    }

    connectedCallback() {
        this.refreshElements();
        if (!this.slider) return;

        this.addEventListeners();
        this.resizeObserver.observe(this.slider);
        this.initPages();
    }

    disconnectedCallback() {
        this.removeEventListeners();
        this.resizeObserver.disconnect();
        this.mutationObserver?.disconnect();
        this.intersectionObserver?.disconnect();
        cancelAnimationFrame(this.sliderRefreshFrame);
    }

    getSlider() {
        return this.querySelector('ul');
    }

    getSliderItems() {
        return this.querySelectorAll('li');
    }

    refreshElements() {
        this.slider = this.getSlider();
        this.sliderItems = this.getSliderItems();
        this.pageCount = this.querySelector('.slider-counter--current');
        this.pageTotal = this.querySelector('.slider-counter--total');
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');
        this.dotButton = this.querySelectorAll('button[name="dots"]');
    }

    refreshSliderItems() {
        this.sliderItems = this.getSliderItems();
    }

    addEventListeners() {
        this.slider.addEventListener('scroll', this.boundUpdate);
        this.prevButton?.addEventListener('click', this.boundButtonClick);
        this.nextButton?.addEventListener('click', this.boundButtonClick);
        this.dotButton.forEach((button) => {
            button.addEventListener('click', this.boundDotClick);
        });
    }

    removeEventListeners() {
        this.slider?.removeEventListener('scroll', this.boundUpdate);
        this.prevButton?.removeEventListener('click', this.boundButtonClick);
        this.nextButton?.removeEventListener('click', this.boundButtonClick);
        this.dotButton.forEach((button) => {
            button.removeEventListener('click', this.boundDotClick);
        });
    }

    observeSliderMutations() {
        this.mutationObserver?.disconnect();
        this.mutationObserver = new MutationObserver(() => {
            this.refreshSliderItems();
            cancelAnimationFrame(this.sliderRefreshFrame);
            this.sliderRefreshFrame = requestAnimationFrame(() => {
                this.initPages();
                this.update();
                this.initButtonDisable?.();
            });
        });
        this.mutationObserver.observe(this.slider, {
            subtree: true,
            childList: true,
        });
    }

    initPages() {
        if (!this.slider || this.sliderItems.length === 0 || !this.sliderItems[0].clientWidth) return;
        this.slidesPerPage = Math.floor(this.slider.clientWidth / this.sliderItems[0].clientWidth);
        this.totalPages = this.sliderItems.length - this.slidesPerPage + 1;
        if (this.pageCount) this.pageCount.innerText = this.slidesPerPage;
    }

    update() {
        if (!this.slider || this.sliderItems.length === 0 || !this.sliderItems[0].clientWidth) return;
        this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItems[0].clientWidth * this.rtl_num);
        this.querySelector('.dots-item.active')?.classList.remove('active');
        this.querySelectorAll('.dots-item')[this.currentPage]?.classList.add('active');
        if (this.pageCount) this.pageCount.innerText = this.slidesPerPage + this.currentPage;
    }

    onButtonClick(event) {
        event.preventDefault();
        if (!this.slider || this.sliderItems.length === 0) return;
        const slideScrollPosition = event.currentTarget.name === 'next' ? this.slider.scrollLeft + this.sliderItems[0].clientWidth * this.rtl_num : this.slider.scrollLeft - this.sliderItems[0].clientWidth * this.rtl_num;
        this.slider.scrollTo({
            left: slideScrollPosition
        });
    }

    onDotClick(event) {
        event.preventDefault();
        this.dotActive = this.querySelector('.dots-item.active button');
        if (!this.dotActive || this.sliderItems.length === 0) return;
        const sliderItemActive = this.dotActive.dataset.index;
        const sliderItem = event.currentTarget.dataset.index;
        this.querySelector('.dots-item.active').classList.remove('active');
        event.currentTarget.closest('.dots-item').classList.add('active');

        const slideScrollPosition = sliderItem * this.sliderItems[0].clientWidth;

        this.slider.scrollTo({
            left: slideScrollPosition
        });
    }
}

if (!customElements.get('slider-component')) {
    customElements.define('slider-component', SliderComponent);
}

class ProductSliderComponent extends SliderComponent {
    connectedCallback() {
        super.connectedCallback();
        if (this.slider) this.observeSliderMutations();
    }

    getSlider() {
        return this.querySelector('.slider');
    }

    getSliderItems() {
        const slides = this.querySelectorAll('.slider__slide');
        return slides.length ? slides : this.querySelectorAll('.slider div.product');
    }
}

class BannerSliderComponent extends SliderComponent {
    constructor() {
        super();
        this.boundSyncInfiniteLoop = this.syncInfiniteLoop.bind(this);
    }

    connectedCallback() {
        super.connectedCallback();

        if (!this.slider) return;

        if (this.hasAttribute('data-loop')) {
            this.loopMedia = window.matchMedia('(max-width: 767px)');
            this.syncInfiniteLoop();
            this.loopMedia.addEventListener('change', this.boundSyncInfiniteLoop);
            this.initButtonDisable();
            return;
        }

        this.observeSliderMutations();
        this.initButtonDisable();
    }

    disconnectedCallback() {
        this.loopMedia?.removeEventListener('change', this.boundSyncInfiniteLoop);
        window.clearTimeout(this.loopResetTimer);
        super.disconnectedCallback();
    }

    getSlider() {
        return this.querySelector('.slider');
    }

    getSliderItems() {
        return this.querySelectorAll('.slider__slide');
    }

    setupInfiniteLoop() {
        if (this.slider.querySelector('[data-loop-clone]')) return;
        this.loopItems = Array.from(this.sliderItems);
        if (this.loopItems.length < 2) return;

        const firstClone = this.loopItems[0].cloneNode(true);
        const lastClone = this.loopItems[this.loopItems.length - 1].cloneNode(true);
        firstClone.dataset.loopClone = 'first';
        lastClone.dataset.loopClone = 'last';
        firstClone.setAttribute('aria-hidden', 'true');
        lastClone.setAttribute('aria-hidden', 'true');
        firstClone.inert = true;
        lastClone.inert = true;

        this.slider.prepend(lastClone);
        this.slider.append(firstClone);
        this.loopTrackItems = [lastClone, ...this.loopItems, firstClone];
        this.loopIndex = 0;

        if (this.hasAttribute('data-loop-initialize-immediately')) {
            this.jumpToLoopTrack(1);
        } else {
            requestAnimationFrame(() => this.jumpToLoopTrack(1));
        }
    }

    teardownInfiniteLoop() {
        this.slider.querySelectorAll('[data-loop-clone]').forEach((clone) => clone.remove());
        this.sliderItems = this.slider.querySelectorAll('.slider__slide');
        this.loopItems = null;
        this.loopTrackItems = null;
        this.loopIndex = 0;
        this.loopAnimating = false;
        window.clearTimeout(this.loopResetTimer);
        this.slider.scrollTo({ left: 0, behavior: 'auto' });
    }

    syncInfiniteLoop() {
        if (this.loopMedia.matches) {
            this.sliderItems = this.slider.querySelectorAll('.slider__slide:not([data-loop-clone])');
            this.setupInfiniteLoop();
        } else {
            this.teardownInfiniteLoop();
        }
    }

    getLoopTrackPosition(trackIndex) {
        const item = this.loopTrackItems?.[trackIndex];
        return item ? item.offsetLeft * this.rtl_num : 0;
    }

    jumpToLoopTrack(trackIndex) {
        if (!this.loopTrackItems?.length) return;
        const previousBehavior = this.slider.style.scrollBehavior;
        this.slider.style.scrollBehavior = 'auto';
        this.slider.scrollTo({ left: this.getLoopTrackPosition(trackIndex), behavior: 'auto' });
        requestAnimationFrame(() => {
            this.slider.style.scrollBehavior = previousBehavior;
        });
    }

    updateLoopDots() {
        const dots = this.querySelectorAll('.dots-item');
        dots.forEach((dot, index) => dot.classList.toggle('active', index === this.loopIndex));
        if (this.pageCount) this.pageCount.innerText = this.loopIndex + 1;
    }

    update() {
        if (this.hasAttribute('data-loop') && this.loopItems?.length) {
            this.updateLoopDots();
            return;
        }
        super.update();
    }

    onButtonClick(event) {
        if (!this.hasAttribute('data-loop')) {
            super.onButtonClick(event);
            return;
        }

        event.preventDefault();
        if (!this.loopItems?.length || this.loopAnimating) return;

        const isNext = event.currentTarget.name === 'next';
        const lastIndex = this.loopItems.length - 1;
        let trackIndex;
        let resetTrackIndex = null;

        if (isNext && this.loopIndex === lastIndex) {
            this.loopIndex = 0;
            trackIndex = this.loopTrackItems.length - 1;
            resetTrackIndex = 1;
        } else if (!isNext && this.loopIndex === 0) {
            this.loopIndex = lastIndex;
            trackIndex = 0;
            resetTrackIndex = this.loopItems.length;
        } else {
            this.loopIndex += isNext ? 1 : -1;
            trackIndex = this.loopIndex + 1;
        }

        this.loopAnimating = true;
        this.slider.scrollTo({
            left: this.getLoopTrackPosition(trackIndex),
            behavior: 'smooth'
        });

        this.updateLoopDots();
        window.clearTimeout(this.loopResetTimer);
        this.loopResetTimer = window.setTimeout(() => {
            if (resetTrackIndex !== null) this.jumpToLoopTrack(resetTrackIndex);
            this.loopAnimating = false;
        }, 420);
    }

    onDotClick(event) {
        if (!this.hasAttribute('data-loop') || !this.loopItems?.length) {
            super.onDotClick(event);
            return;
        }

        event.preventDefault();
        const index = Number(event.currentTarget.dataset.index);
        if (!Number.isInteger(index) || index < 0 || index >= this.loopItems.length) return;
        this.loopIndex = index;
        this.slider.scrollTo({
            left: this.getLoopTrackPosition(index + 1),
            behavior: 'smooth'
        });
        this.updateLoopDots();
    }
    
    initButtonDisable() {
        this.intersectionObserver?.disconnect();

        if (this.hasAttribute('data-loop')) {
            this.prevButton?.classList.remove('disabled');
            this.nextButton?.classList.remove('disabled');
            return;
        }
        if (!this.slider || this.sliderItems.length === 0) return;

        this.slider.querySelectorAll('.first-item').forEach((item) => item.classList.remove('first-item'));
        this.slider.querySelectorAll('.last-item').forEach((item) => item.classList.remove('last-item'));

        this.intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    if (entry.target.classList.contains('first-item')) {
                        this.prevButton?.classList.add('disabled')    
                    } else if (entry.target.classList.contains('last-item')) {
                        this.nextButton?.classList.add('disabled')    
                    }
                } else {
                    if (entry.target.classList.contains('first-item')) {
                        this.prevButton?.classList.remove('disabled')   
                    } else if (entry.target.classList.contains('last-item')) {
                        this.nextButton?.classList.remove('disabled')   
                    }
                }
            })
        }, {
            threshold: 0.8,
            root: this.slider 
        })

        this.sliderItems[0].classList.add('first-item')
        this.sliderItems[this.sliderItems.length - 1].classList.add('last-item')

        this.intersectionObserver.observe(this.sliderItems[0])
        this.intersectionObserver.observe(this.sliderItems[this.sliderItems.length - 1])
    }
}

class TiktokSliderComponent extends BannerSliderComponent {
    constructor() {
        super();

        this.sliderActionsAndPageCount = this.querySelector('.slider-buttons');
    }

    initPages() {
        if (this.sliderItems.length === 0) return;
        const distance = this.slider.clientWidth / this.sliderItems[0].clientWidth;

        this.slidesPerPage = distance % 1 > 0.5 ? Math.ceil(distance) : Math.floor(distance);
        this.totalPages = Math.ceil(this.sliderItems.length - this.slidesPerPage) + 1;

        if (this.pageTotal && this.dataset.tiktokSlider === 'true') {
            this.pageTotal.innerText = this.totalPages;
        }   

        if (this.totalPages <= 1 || window.innerWidth >= 1025) {
            this.sliderActionsAndPageCount.style.display = 'none';
        } else if (this.totalPages > 1) this.sliderActionsAndPageCount.style.display = 'flex';
    }   
}

window.addEventListener('load', () => {
    if (!customElements.get('banner-slider-component')) {
        customElements.define('banner-slider-component', BannerSliderComponent);
    }
    if (!customElements.get('product-slider-component')) {
        customElements.define('product-slider-component', ProductSliderComponent);
    }
    if (!customElements.get('tiktok-slider-component')) {
        customElements.define('tiktok-slider-component', TiktokSliderComponent);
    }
})
